// src/components/Navbar.tsx
//
// Site navigation.
//
//   <nav aria-label="Primary">
//     Brand -> Home
//     DesktopNav  (>= 1024px)  : brand | links | Query CTA + theme toggle
//     MobileBar   (<  1024px)  : brand | theme toggle + menu button, panel on demand
//
// Design notes:
//
// - THE THEME TOGGLE IS IN THE BAR, NOT THE PANEL. It renders at both
//   breakpoints, which is why the trailing controls share NavActions: the
//   desktop row pairs it with the Query CTA and the mobile bar pairs it with
//   the menu button. Putting it inside the mobile panel would mean changing the
//   palette required opening the menu first, which is a pointless extra step for
//   a control that is always the same size.
//
// - ROUTES ARE DECLARED ONCE, in NAV_ITEMS, and read by both the desktop list
//   and the mobile panel. The old version derived a route from a label by
//   lowercasing it ("Home" -> "/home"), which meant the canonical "/" route
//   matched nothing and "Home" was never highlighted on the home page.
//
// - ALIASES. /home and /view-my-work are real routes that render the same pages
//   as / and /projects. Matching on the `to` path alone would leave the nav
//   unhighlighted when a visitor lands on an alias directly, so each item can
//   declare the paths that should light it up.
//
// - DESKTOP/MOBILE IS A JS BRANCH, not a CSS one, so the navigation list is
//   never duplicated in the DOM for screen readers. Same rule as
//   WhatIDoSection and ExperienceHighlights.
//
// - "Lets Connect" IS NOT IN NAV_ITEMS, because it is an action rather than a
//   route. It opens the contact window in place via ContactModalContext, for the
//   same reason the Query CTA does. A visitor on the Projects page who wants to
//   get in touch should not lose their scroll position to a route change just to
//   arrive at a form. /contacts still exists and renders the same form as a
//   standalone page for direct links and bookmarks.
//
// - THE MENU CLOSES WITHOUT A LOCATION WATCH. Every close path calls `close()`,
//   which also returns focus to the toggle. React Router does not move focus on
//   navigation, so a route change alone cannot be relied on to close the panel.
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useLocation } from 'react-router-dom';

import { siteConfig } from '../config/siteConfig';
import { EASE_OUT, DURATION } from './about/motion';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { useQueryModal } from '../context/QueryModalContext';
import { useContactModal } from '../context/ContactModalContext';
import ThemeToggle from './ThemeToggle';
import {
  DESKTOP_QUERY,
  NavRoot,
  NavInner,
  Brand,
  BrandName,
  BrandRole,
  NavList,
  NavItem,
  NavLink,
  NavAction,
  NavActions,
  QueryCta,
  MenuButton,
  MenuIcon,
  Backdrop,
  MobilePanel,
  MobileInner,
  MobileList,
  MobileItem,
  MobileLink,
  MobileMarker,
  MobileAction,
  MobileCta,
} from './Navbar.styles';

interface NavItemConfig {
  label: string;
  /** Canonical route. This is what the link navigates to. */
  to: string;
  /**
   * Extra paths that render the same page, so the active state stays correct
   * when a visitor lands on one of them directly.
   */
  aliases?: readonly string[];
}

const NAV_ITEMS: readonly NavItemConfig[] = [
  { label: 'Home', to: '/', aliases: ['/home'] },
  { label: 'About', to: '/about' },
  { label: 'Projects', to: '/projects', aliases: ['/view-my-work'] },
  { label: 'Extensions', to: '/extensions' },
];

/**
 * Kept out of NAV_ITEMS, alongside QUERY_LABEL: it opens a window rather than
 * navigating, so it carries no route and can never be a current page.
 */
const CONTACT_LABEL = 'Lets Connect';

/** Kept out of NAV_ITEMS: it is an action, not navigation. */
const QUERY_LABEL = 'Start a Conversation';

/** Compact identity line. `name` comes from siteConfig so it stays in step. */
const BRAND_ROLE = 'Backend Engineer';

const MOBILE_PANEL_ID = 'navbar-mobile-panel';

/** `/about/` and `/about` are the same page. */
const normalisePath = (path: string): string =>
  path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;

/**
 * True when `pathname` should light up `item`.
 *
 * Exact match, plus a segment-boundary prefix match so nested routes such as
 * `/projects/anything` still mark Projects as current. The boundary check is
 * what stops `/projectssomething` from matching `/projects`.
 */
const isCurrentPath = (pathname: string, item: NavItemConfig): boolean => {
  const path = normalisePath(pathname);
  const candidates = [item.to, ...(item.aliases ?? [])].map(normalisePath);

  return candidates.some((candidate) =>
    candidate === '/' ? path === '/' : path === candidate || path.startsWith(`${candidate}/`)
  );
};

const Navbar = () => {
  const location = useLocation();
  const isDesktop = useMediaQuery(DESKTOP_QUERY);
  const reduceMotion = useReducedMotion() ?? false;
  const { openQueryModal } = useQueryModal();
  const { openContactModal } = useContactModal();

  const [isOpen, setIsOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);

  // The panel is only reachable below the desktop breakpoint. When the viewport
  // grows past it the panel unmounts, so a stale `isOpen` cannot resurface.
  const menuOpen = !isDesktop && isOpen;

  /**
   * Closing always returns focus to the toggle. Without this, activating a menu
   * link unmounts the focused element and drops focus to <body>, stranding
   * keyboard and screen reader users at the top of the document.
   */
  const close = (returnFocus = true) => {
    setIsOpen(false);
    if (returnFocus) toggleRef.current?.focus();
  };

  const toggle = () => setIsOpen((open) => !open);

  // Escape closes the menu from anywhere inside the nav, so no window-level
  // listener is needed and focus never has to leave the component.
  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Escape' && menuOpen) {
      event.stopPropagation();
      close();
    }
  };

  // Stop the page behind the panel from scrolling. The fixed backdrop already
  // blocks pointer events; this covers touch and wheel.
  useEffect(() => {
    if (!menuOpen) return undefined;

    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previous;
    };
  }, [menuOpen]);

  const panelMotion = reduceMotion
    ? {
        initial: { height: 0 as const, opacity: 0 },
        animate: { height: 'auto' as const, opacity: 1 },
        exit: { height: 0 as const, opacity: 0 },
        transition: { duration: 0 },
      }
    : {
        initial: { height: 0 as const, opacity: 0 },
        animate: { height: 'auto' as const, opacity: 1 },
        exit: { height: 0 as const, opacity: 0 },
        // Height rather than a slide: the panel is full width, so translating
        // it on Y would look like a curtain. This reuses the shared easing and
        // duration scale so it stays inside the site's motion language.
        transition: { duration: DURATION.panelIn, ease: EASE_OUT },
      };

  return (
    <NavRoot aria-label="Primary" onKeyDown={handleKeyDown}>
      <NavInner>
        <Brand to="/" aria-label={`${siteConfig.name} — home`}>
          <BrandName>{siteConfig.name}</BrandName>
          <BrandRole>{BRAND_ROLE}</BrandRole>
        </Brand>

        {isDesktop ? (
          <>
            <NavList>
              {NAV_ITEMS.map((item) => {
                const $active = isCurrentPath(location.pathname, item);

                return (
                  <NavItem key={item.to}>
                    <NavLink
                      to={item.to}
                      $active={$active}
                      aria-current={$active ? 'page' : undefined}
                    >
                      {item.label}
                    </NavLink>
                  </NavItem>
                );
              })}

              {/* The last item in the list, but an action rather than a route.
                  It keeps the surrounding <li> so the list stays a list, and it
                  is announced as a button that opens a dialog. */}
              <NavItem>
                <NavAction
                  type="button"
                  aria-haspopup="dialog"
                  onClick={openContactModal}
                >
                  {CONTACT_LABEL}
                </NavAction>
              </NavItem>
            </NavList>

            <NavActions>
              <QueryCta label={QUERY_LABEL} onClick={openQueryModal} />
              <ThemeToggle />
            </NavActions>
          </>
        ) : (
          /* The mobile bar is branch, not CSS: brand, then the two controls.
             The theme toggle is deliberately here rather than inside the panel
             so the palette can be changed without opening the menu first. */
          <NavActions>
            <ThemeToggle />
            <MenuButton
              ref={toggleRef}
              type="button"
              aria-expanded={menuOpen}
              aria-controls={MOBILE_PANEL_ID}
              aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              onClick={toggle}
            >
              <MenuIcon $open={menuOpen} aria-hidden="true" />
            </MenuButton>
          </NavActions>
        )}
      </NavInner>

      <AnimatePresence initial={false}>
        {menuOpen && (
          <motion.div
            key="navbar-mobile-panel"
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <Backdrop
              aria-hidden="true"
              onClick={() => close(false)}
              {...panelMotion}
            />

            <MobilePanel id={MOBILE_PANEL_ID} {...panelMotion}>
              <MobileInner>
                <MobileList>
                  {NAV_ITEMS.map((item) => {
                    const $active = isCurrentPath(location.pathname, item);

                    return (
                      <MobileItem key={item.to}>
                        <MobileLink
                          to={item.to}
                          $active={$active}
                          aria-current={$active ? 'page' : undefined}
                          onClick={() => close()}
                        >
                          {item.label}
                          <MobileMarker $active={$active} aria-hidden="true" />
                        </MobileLink>
                      </MobileItem>
                    );
                  })}

                  {/* Same action as the desktop NavAction, carrying the
                      panel's row rhythm. Closing first and opening second keeps
                      the two transitions in order: the panel is gone before the
                      window takes focus, and the window hands focus back to the
                      toggle on close. */}
                  <MobileItem>
                    <MobileAction
                      type="button"
                      aria-haspopup="dialog"
                      onClick={() => {
                        close();
                        openContactModal();
                      }}
                    >
                      {CONTACT_LABEL}
                    </MobileAction>
                  </MobileItem>
                </MobileList>

                {/* Closes the menu and opens the window in one action. The
                    modal takes focus on open and hands it back to the toggle
                    on close, so the sequence leaves focus in a sensible
                    place at both ends. */}
                <MobileCta
                  label={QUERY_LABEL}
                  onClick={() => {
                    close();
                    openQueryModal();
                  }}
                />
              </MobileInner>
            </MobilePanel>
          </motion.div>
        )}
      </AnimatePresence>
    </NavRoot>
  );
};

export default Navbar;
